import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  differenceInYears,
  format,
  formatDate,
  isValid,
  parse,
} from 'date-fns';
import { SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useSnapshot } from 'valtio';
import * as yup from 'yup';

import { BackOfficePermissionsRedirectWrapper } from '../../../components/pages/backoffice/permissions-redirect-wrapper.jsx';
import CancelButton from '../../../components/buttons/cancel-button.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { DateInputWithDatePicker } from '../../../components/ui/date-picker.jsx';
import FormCard from '../../../components/ui/form-card.jsx';
import FormFooter from '../../../components/ui/form-footer.jsx';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../components/ui/form.jsx';
import { Input } from '../../../components/ui/input.jsx';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { DISTRICTS } from '../../../utils/constants.js';
import { formatDateToISO } from '../../../utils/format.js';
import { REGEX_DATE_EU } from '../../../utils/regex.js';

export function CustomersPendingConsentEdit() {
  const navigate = useNavigate();
  const { pendingConsentId } = useParams();
  const { user } = useSnapshot(authState);
  const [
    currentCustomerIdentificationDoc,
    setCurrentCustomerIdentificationDoc,
  ] = useState(null);
  const [currentCustomerEmail, setCurrentCustomerEmail] = useState(null);

  const editCustomerPendingConsentForm = useForm({
    mode: 'onChange',
    defaultValues: {
      firstName: undefined,
      lastName: undefined,
      cc: undefined,
      email: undefined,
      birthDate: undefined,
      phone: undefined,
      location: undefined,
      address: undefined,
      district: undefined,
      zipCode: undefined,
    },
    resolver: yupResolver(
      yup.object().shape({
        firstName: yup
          .string()
          .min(2, 'Nome tem de ter pelo menos 2 caracteres')
          .required('Nome é um campo obrigatório'),
        lastName: yup
          .string()
          .min(2, 'Apelido tem de ter pelo menos 2 caracteres')
          .required('Apelido é um campo obrigatório'),
        cc: yup
          .string()
          .min(6, 'Nº Identificação tem de ter pelo menos 6 caracteres')
          .required('Nº Identificação é um campo obrigatório'),
        birthDate: yup
          .string()
          .required('Data de Nascimento é um campo obrigatório')
          .matches(
            REGEX_DATE_EU,
            'Data de Nascimento possui um formato inválido',
          )
          .test('validDate', 'Insira uma data válida', function (value) {
            try {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());
              const isValidDate = isValid(parsedDate);

              return isValidDate;
            } catch (error) {
              return false;
            }
          })
          .test(
            'dob',
            'Apenas clientes com 18 anos ou mais podem participar na campanha',
            function (value) {
              const parsedDate = parse(value, 'dd-MM-yyyy', new Date());

              return differenceInYears(new Date(), parsedDate) >= 18;
            },
          ),
        phone: yup
          .string()
          .required('Telemóvel é um campo obrigatório')
          .matches(
            /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/,
            'Nº de Telemóvel inválido',
          ),
        location: yup.string().required('Localidade é um campo obrigatório'),
      }),
    ),
  });

  const queryPendingConsentCustomer = useQuery({
    queryKey: ['customer_pendingConsent'],
    queryFn: async () => {
      const { data: getData } = await msCatalystApi.get(
        `/customers/consent-requests/${pendingConsentId}`,
      );

      getData.data.customer = JSON.parse(getData.data.customer);

      setCurrentCustomerIdentificationDoc(getData.data.cc);
      setCurrentCustomerEmail(getData.data.email);

      editCustomerPendingConsentForm.reset({
        cc: getData.data.customer.cc,
        firstName: getData.data.customer.firstName,
        lastName: getData.data.customer.lastName,
        birthDate: format(getData.data.customer.birthDate, 'dd-MM-yyyy'),
        email: getData.data.customer.email,
        phone: getData.data.customer.phone,
        location: getData.data.customer.location,
        address: getData.data.customer.address,
        district: getData.data.customer.district,
        zipCode: getData.data.customer.zipCode,
      });

      return getData.data;
    },
  });

  const mutationUpdateCustomerConsentRequest = useMutation({
    mutationFn: async (data) => {
      const { data: updateCustomerConsentRequestRes } = await msCatalystApi.put(
        `/customers/consent-requests/${pendingConsentId}`,
        {
          customer: JSON.stringify(data),
          staff: `${user.firstName} ${user.lastName}`,
          searchCustomerIdentificationDoc: data.cc,
          searchCustomerEmail: data.email,
          currentCustomerIdentificationDoc,
          currentCustomerEmail,
        },
      );

      return updateCustomerConsentRequestRes.data;
    },
  });

  const onFormSubmit = async (data) => {
    try {
      const editedCustomerConsentRequest =
        await mutationUpdateCustomerConsentRequest.mutateAsync({
          ...data,
          birthDate: formatDateToISO(data.birthDate),
        });

      toast.success(
        'Atualização de Cliente Pendente de Consentimento com sucesso',
      );

      navigate(
        `/backoffice/customers/pending-consent/${editedCustomerConsentRequest.ROWID}`,
      );
    } catch (err) {
      toast.error('Erro ao atualizar Cliente Pendente de Consentimento');
    }
  };

  if (queryPendingConsentCustomer.isLoading) {
    return <Loading page={true} />;
  }

  if (queryPendingConsentCustomer.isError) {
    return <Error />;
  }

  return (
    <BackOfficePermissionsRedirectWrapper>
      <div className="pb-6">
        <Heading className="pb-2">
          Editar Cliente Pendente de Consentimento
        </Heading>
        <h2 className="text-lg text-zinc-600 dark:text-zinc-400">
          {queryPendingConsentCustomer.data.fullName}
        </h2>
      </div>

      <Form {...editCustomerPendingConsentForm}>
        <form
          onSubmit={editCustomerPendingConsentForm.handleSubmit(onFormSubmit)}
        >
          <div className="flex flex-col space-y-10">
            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Dados Gerais</h2>

              <FormCard>
                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nome</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Apelido</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="cc"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Nº Identificação</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="birthDate"
                  render={({ field }) => {
                    let selectedDate = null;

                    if (field?.value?.match(REGEX_DATE_EU)) {
                      const [day, month, year] = field.value.split('-');
                      selectedDate = new Date(`${year}-${month}-${day}`);
                    }

                    return (
                      <FormItem>
                        <FormLabel required={true}>
                          Data de Nascimento
                        </FormLabel>
                        <FormControl>
                          <DateInputWithDatePicker
                            input={{
                              ...field,
                              onChange: (e) => {
                                const date = e.target.value;

                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(date);
                              },
                            }}
                            calendar={{
                              selected: selectedDate,
                              onSelect: (date) => {
                                if (!date && !isValid(new Date(date))) return;

                                field.onChange(
                                  formatDate(new Date(date), 'dd-MM-yyyy'),
                                );
                              },
                            }}
                          />
                        </FormControl>

                        <FormDescription>
                          Data de Nascimento tem de seguir o formato:{' '}
                          <i>dd-MM-yyyy</i>
                        </FormDescription>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Telemóvel</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>

            <div className="flex flex-col gap-5">
              <h2 className="text-xl font-medium">Localização</h2>

              <FormCard>
                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required={true}>Localidade</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Morada</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="district"
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormLabel>Distrito</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue>{field.value}</SelectValue>
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {DISTRICTS.map((item, i) => {
                              return (
                                <SelectItem key={i} value={item}>
                                  {item}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />

                <FormField
                  control={editCustomerPendingConsentForm.control}
                  name="zipCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Código Postal</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </FormCard>
            </div>
          </div>

          <FormFooter>
            <CancelButton />
            <Button
              type="submit"
              disabled={
                !editCustomerPendingConsentForm.formState.isDirty ||
                editCustomerPendingConsentForm.formState.isSubmitting
              }
              isLoading={editCustomerPendingConsentForm.formState.isSubmitting}
            >
              <SaveIcon />
              Guardar Alterações
            </Button>
          </FormFooter>
        </form>
      </Form>
    </BackOfficePermissionsRedirectWrapper>
  );
}
